/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import {
    IAddToOrderTemplateDialogResources,
    IDuplicateItemsWhenAddingToOrderTemplateDialogResources,
    IItemAddedToOrderTemplateDialogResources,
    ILinesAddedToOrderTemplateDialogResources,
    IOrderTemplateNameDialogResources,
    IWishlistActionSuccessResult,
    PromoCodeComponent,
    SaveCartToOrderTemplateComponent
} from '@msdyn365-commerce/components';
import MsDyn365, { getCatalogId, getUrlSync, IImageSettings, isChannelTypeB2B, getAsset } from '@msdyn365-commerce/core';
import { ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import { AsyncResult, Cart as CartType, CartLine, CartLineValidationResultsByLineId, LineValidationResult, format } from '@msdyn365-commerce/retail-proxy';
import { FeatureState, ProductDeliveryOptions, ProductList, ProductPrice, ProjectionDomain, ReleasedProductType, UnitOfMeasureConversion } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';
import { validateCartAsync } from '@msdyn365-commerce-modules/cart-utilities';
import { IInvoiceSummaryLines, InvoiceSummary } from '@msdyn365-commerce-modules/invoice-payment-summary';
import { OrderSummary } from '@msdyn365-commerce-modules/order-summary-utilities';
import {
    ActiveCartProductsInput,
    ArrayExtensions,
    getActiveCartProductsAction,
    getOrderTemplates,
    GetOrderTemplatesInput,
    ICartLineInventoryValidationResult,
    ObjectExtensions,
    OrderTemplate,
    QueryResultSettingsProxy,
    validateCartLinesInventory, validateCatalogId
} from '@msdyn365-commerce-modules/retail-actions';
import {
    Alert,
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    INodeProps,
    IPayLoad,
    ITelemetryContent,
    TelemetryConstant,
    Waiting,
    updateMaxQuantityForCartLineItem,
    urlCheck
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import { debounce } from 'lodash';
import React from 'react';

import { IDlvryCartData } from './dlvry-cart.data';
import { IDlvryCartProps, IDlvryCartResources } from './dlvry-cart.props.autogenerated';
import { CartLineGroupItems } from './components/dlvry-cart-line-groups';
import { CartLineItems, ICartlinesViewProps } from './components/dlvry-cart-line-items';
import { cartPromotions, ICartLineItemViewProps } from './components/dlvry-cart-line-items-promotion';
import { getPromotionData, IPromotionData } from './utils/get-promotion-in-dlvry-cart';
import ImportModal from './components/dlvry-cart-import-modal';
import { getProductUnitOfMeasureOptionsAsync } from '../../actions/get-product-unit-of-measure-options.action';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getDiscountBadge } from '../../shared-utils/badge-utils';
import { IProductInventoryInformation } from '../../components/common/product-inventory-utils';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { CalculationModes, updateCalcCartLinesAsync } from '../../actions/update-calc-cart-lines.action';
import { toasts } from '../../components/toast/toast';
import { IOrderSummaryLinesExt, SimpleProductExt } from '../../shared-utils/types';
import { getShipCarrierAddressAsync } from '../../actions/get-ship-carrier-address.action';
import { IProductSelection, ProductListProduct } from '../../components/product-list-component/product-list-component';
import { getInitialBreakoffState } from '../../components/common/product-list-order-quantity';
import ConfirmationService from '../../shared-utils/confirmation/ConfirmationService';
import { IProductListByProductId } from '../../actions/get-product-lists-by-product-ids';
import * as GTM from '../../shared-utils/google-tag-manager/gtm';
import { getAllCharges } from '../../shared-utils/cart-utils';
import { isAnonymousCheckoutEnabled } from './Common/utilities';

/**
 * Cart view props interface.
 */
export interface ICartViewProps extends IDlvryCartProps<IDlvryCartData> {
    className: string;
    title?: React.ReactNode;
    actionBar?: React.ReactNode;
    CartlinesWrapper: INodeProps;
    cartlines?: ICartlinesViewProps[];
    cartLinesGroup?: ICartlinesViewProps[][];
    cart: ICartState | undefined;
    orderSummaryHeading: React.ReactNode;
    orderSummaryLineitems?: IOrderSummaryLinesExt;
    invoiceSummaryLineitems?: IInvoiceSummaryLines;
    OrderSummaryWrapper: INodeProps;
    OrderSummaryItems: INodeProps;
    OrderSummaryErrors?: IOrderSummaryErrors;
    checkoutAsGuestButton: React.ReactNode;
    checkoutAsSignInUserButton: React.ReactNode;
    importModal?: React.ReactNode;
    storeSelector?: React.ReactNode;
    expressCheckoutButton?: React.ReactNode[];
    ExpressCheckoutSectionWrapper: INodeProps;
    backToShoppingButton: React.ReactNode;
    waitingComponent: React.ReactNode;
    promotionOptions?: ICartLineItemViewProps;
    promoCode: React.ReactNode;
    cartLoadingStatus: string;
    cartDataResult: boolean;
    multiplePickUpEnabled?: boolean;
    telemetryContent?: ITelemetryContent;
    checkoutBlockedDueToUnavailableFunds?: React.ReactNode;
    searchBar: React.ReactNode;
    searchTerm: string;
    unitConversions: UnitOfMeasureConversion[];
    activePrices: ProductPrice[];
    productAvailabilites: IProductInventoryInformation[] | undefined;
    breakoffProducts: { [key: number]: boolean };
    removeItemClickHandler(cartlineToRemove: CartLine): void;
    moveToWishlistSuccessHandler(result: IWishlistActionSuccessResult, cartlineId: CartLine): void;
    onBreakoffChange(product: ProductListProduct, enabled: boolean): void;
    selectedProducts: IProductSelection;
    onUpdateSelection: (selection: IProductSelection) => void;
    updateCartlineQuantity: (cartLine: CartLine, quantity: number) => void;
    onUpdateOrderTemplates: ((product?: ProductListProduct, orderTemplate?: ProductList) => Promise<OrderTemplate[]>) | undefined;
    retrieveOrderTemplates: () => Promise<OrderTemplate[]>;
    isUpdatingQuantity?: boolean;
}

/**
 * Order summary errors interface.
 */
export interface IOrderSummaryErrors {
    Wrapper: INodeProps;
    header: React.ReactNode;
    errors: React.ReactNode[];
}

/**
 * Cart component state interface.
 */
interface ICartComponentState {
    showPromotionDialog: boolean;
    cartlinesErrors: CartLineValidationResultsByLineId;
    promotionData: IPromotionData;
    isCartValid: boolean;
    productPickUpOptions?: ProductDeliveryOptions[];
    searchTerm: string;
    showImportModal: boolean;
    unitConversions: UnitOfMeasureConversion[];
    activePrices: ProductPrice[];
    isRecalculatingCartPrices: boolean;
    isUpdatingQuantity?: boolean;
    selectedProducts?: IProductSelection;
    breakoffProducts: { [key: number]: boolean };
    orderTemplates?: OrderTemplate[];
}

/**
 * Cart component.
 * @extends {React.PureComponent<IDlvryCartProps<IDlvryCartData>>}
 */
@observer
class Cart extends React.Component<IDlvryCartProps<IDlvryCartData>> {
    public state: ICartComponentState;

    private readonly telemetryContent: ITelemetryContent;

    private readonly payLoad: IPayLoad;

    private isQuantityLimitsFeatureEnabled: boolean = false;

    private _isMounted: boolean = false;

    private readonly promotionPopupId: string = 'cart-promotion-popup';

    private readonly cartLineQuantityUpdatedAsyncDebounced = debounce(this.updateCartLineQuantityAsync, 1000);

    public constructor(props: IDlvryCartProps<IDlvryCartData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.payLoad = getPayloadObject('click', this.telemetryContent, '');
        this.promotionLinkClick = this.promotionLinkClick.bind(this);
        this.promotionCloseButtonclick = this.promotionCloseButtonclick.bind(this);

        const breakoffProducts: { [key: number]: boolean } = {};
        if (this.props.data.products.result && this.props.data.products.result.length > 0) {
            this.props.data.products.result.forEach(product => {
                const cartLine = this.props.data.cart.result?.cart.CartLines?.find(line => line.ProductId === product.RecordId);
                if (cartLine && cartLine.Quantity) {
                    const breakoffState = getInitialBreakoffState(cartLine.Quantity, product);
                    if (breakoffState.breakoff) {
                        breakoffProducts[product.RecordId] = breakoffState.breakoff;
                    }
                }
            });
        }

        this.state = {
            showPromotionDialog: false,
            promotionData: {},
            isCartValid: true,
            productPickUpOptions: undefined,
            cartlinesErrors: {},
            searchTerm: '',
            showImportModal: false,
            unitConversions: [],
            activePrices: [],
            isRecalculatingCartPrices: false,
            selectedProducts: {},
            breakoffProducts
        };
    }

    /**
     * Component did mount function.
     */
    public async componentDidMount(): Promise<void> {
        this._isMounted = true;
        const {
            config,
            context: { actionContext }
        } = this.props;

        if (MsDyn365.isBrowser) {
            ConfirmationService.init();
        }

        this.isQuantityLimitsFeatureEnabled =
            (await this.getOrderQuantityLimitsFeatureFlag()) || (await this._isChannelLimitsFeatureEnabled());
        if (this.isQuantityLimitsFeatureEnabled) {
            await this.validateCart();
        }

        void this.props.data.orderTemplates.then(orderTemplates => {
            this.setState({ orderTemplates });
        });

        if (config.showAvailablePromotions) {
            if (this._isMounted && this.props.data.cart.result?.cart.CartLines) {
                this.setState({ promotionData: await getPromotionData(actionContext) });
            }

            reaction(
                () => this.props.data.cart.result?.cart.CartLines,
                async () => {
                    if (this._isMounted) {
                        this.setState({ promotionData: await getPromotionData(actionContext) });
                    }
                }
            );
        }

        reaction(
            () => this.props.data.cart.result?.cart.CartLines,
            async () => {
                if (this._isMounted) {
                    /* const input = new ProductPickUpOptionsForCartLineItems(actionContext.requestContext.apiSettings);
                    this.setState({ productPickUpOptions: await getPickUpOptionsForCartLineItems(input, actionContext) }); */
                    void this._getUnitConversions();
                }
            }
        );

        void this._getUnitConversions();
        const getActivePricesPromise = this._getActivePrices();
        void this._recalculateCartPrices();

        // Wait for cart, products and active prices to be available before sending GTM view_cart event
        void Promise.all([
            this.props.data.cart,
            this.props.data.products,
            getActivePricesPromise
        ]).then(([cart, products]) => {
            void GTM.viewCartEventAsync(cart.cart, products, this.state.activePrices);
        });
    }

    /**
     * Component will unmount function.
     */
    public componentWillUnmount(): void {
        this._isMounted = false;
    }

    /**
     * Should component update function.
     * @param nextProps - Next props.
     * @param nextState - Next state.
     * @returns True if component should update, false otherwise.
     */
    public shouldComponentUpdate(nextProps: IDlvryCartProps<IDlvryCartData>, nextState: ICartComponentState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    /* eslint-disable complexity -- Disabling complexity for render method */
    public render(): JSX.Element | null {
        const {
            data: {
                cart: { result: cart },
                products: { result: products }
            },
            context: {
                request: {
                    user: { isAuthenticated, signInUrl }
                }
            },
            resources
        } = this.props;

        const oun = this.props.context.request.apiSettings.oun.toLowerCase();
        const disableInventoryCheckForCompanies = (this.props.config.disableInventoryCheckForCompany || '').split(',').map(x => x.trim().toLowerCase());
        const guestCheckoutUrl = getUrlSync('checkout', this.props.context.actionContext) || '';
        const backToShoppingUrl = getUrlSync('home', this.props.context.actionContext) || '';
        const checkoutURL = this.getCheckoutURL(isAuthenticated, guestCheckoutUrl, signInUrl || '');
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
        const cartLines = this._getCartLines();
        const emailDeliveryModeCode = this.props.context.request.channel?.EmailDeliveryModeCode;
        const validInventoryAcrossCartLines = validateCartLinesInventory(
            cart?.cart.CartLines ?? [],
            disableInventoryCheckForCompanies.includes(oun) ? [] : this.props.data.productAvailabilites.result ?? [],
            this.props.context.actionContext,
            products ?? [],
            emailDeliveryModeCode
        );

        const hasError =
                !this.state.isCartValid ||
                (cartLines ? cartLines.some(cartline => cartline.hasError) : false);

        // const hasError =
        //     !validInventoryAcrossCartLines?.isValid ||
        //     this._isCheckoutBlockedDueToUnavailableFunds() ||
        //     (!validInventoryAcrossCartLines && !this.state.isCartValid) ||
        //     (cartLines ? cartLines.some(cartline => cartline.hasError) : false);
        const RetailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );
        const multiplePickUpEnabled = RetailMultiplePickupFeatureState?.IsEnabled;
        const currentCatalogId = getCatalogId(this.props.context.request);
        validateCatalogId(currentCatalogId);
        const cartLinesGroup = cart?.hasInvoiceLine || multiplePickUpEnabled ? this.getCartLinesGroup() : null;
        this.payLoad.contentAction.etext = TelemetryConstant.Checkout;
        const checkoutBySignInAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.GuestCheckout;
        const checkoutByGuestAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.BackToShopping;
        const backtoShoppingAttributes = getTelemetryAttributes(this.telemetryContent, this.payLoad);

        // If there's inventory issues across multiple cart lines then update the error and display as part of the order summary
        if (validInventoryAcrossCartLines && !validInventoryAcrossCartLines.isValid) {
            if (multiplePickUpEnabled && cartLinesGroup !== null) {
                for (const cartLineGroup of cartLinesGroup) {
                    this._updateCartLineErrorForInvalidInventory(cartLineGroup, validInventoryAcrossCartLines);
                }
            }

            this._updateCartLineErrorForInvalidInventory(cartLines, validInventoryAcrossCartLines);
        }

        const isAnonymousCheckoutEnabled = this._isAnonymousCheckoutEnabled();

        const viewProps = {
            ...this.props,
            className: classnames('ms-cart'),
            title: <h1 className='msc-cart__heading'>{resources.cartTitle}</h1>,
            cart: this.props.data.cart.result,
            CartlinesWrapper: { className: 'msc-cartline-wraper' },
            OrderSummaryWrapper: { className: cart?.hasInvoiceLine ? 'msc-invoice-summary-wrapper' : 'msc-order-summary-wrapper' },
            OrderSummaryItems: { className: cart?.hasInvoiceLine ? 'msc-invoice-summary__items' : 'msc-order-summary__items' },
            cartlines: cartLines,
            cartLinesGroup,
            productAvailabilites: this._getProductAvailabilities(),
            actionBar: this._renderActionBar(),
            importModal: this._renderImportModal(),
            promoCode: this._getPromoCode(),
            promotionOptions: this._getPromotions(),
            context: this.props.context,
            typeName: this.props.typeName,
            telemetry: this.props.telemetry,
            id: this.props.id,
            multiplePickUpEnabled,
            removePromoAriaLabelFormat: resources.removePromoAriaLabelFormat,
            orderSummaryHeading: ArrayExtensions.hasElements(cart?.cart.CartLines) ? (
                cart?.hasInvoiceLine ? (
                    <h2 className='msc-invoice-summary__heading'>{resources.invoiceSummaryTitle}</h2>
                ) : (
                    <h2 className='msc-order-summary__heading'>{resources.orderSummaryTitle}</h2>
                )
            ) : null,
            orderSummaryLineitems: this._getOrderSummaryLineItems(),
            invoiceSummaryLineitems: this._getInvoiceSummaryLineItems(),
            checkoutAsSignInUserButton: !isCartEmpty ? (
                <Button
                    /* disabled={hasError} */
                    className={classnames('msc-cart__btn-checkout')}
                    href={checkoutURL}
                    title={resources.checkoutButtonTitle}
                    {...checkoutBySignInAttributes}
                >
                    {resources.checkoutButtonTitle}
                </Button>
            ) : null,
            checkoutAsGuestButton:
                !isCartEmpty && !isAuthenticated && isAnonymousCheckoutEnabled ? (
                    <Button
                        disabled={hasError}
                        className='msc-cart__btn-guestcheckout'
                        href={guestCheckoutUrl}
                        title={resources.guestCheckoutButtonTitle}
                        {...checkoutByGuestAttributes}
                    >
                        {resources.guestCheckoutButtonTitle}
                    </Button>
                ) : null,
            expressCheckoutButton: !isCartEmpty ? this.props.slots.paymentExpress : undefined,
            ExpressCheckoutSectionWrapper: { className: 'msc-express-payment-container-in-cart-page' },
            backToShoppingButton: (
                <Button
                    className='msc-cart__btn-backtoshopping'
                    {...backtoShoppingAttributes}
                    href={backToShoppingUrl}
                    title={resources.backToShoppingButtonTitle}
                >
                    {resources.backToShoppingButtonTitle}
                </Button>
            ),
            checkoutBlockedDueToUnavailableFunds: null,
            searchBar: this._getSearchBar(),
            searchTerm: this.state.searchTerm,
            storeSelector:
                this.props.slots.storeSelector && this.props.slots.storeSelector.length > 0 ? this.props.slots.storeSelector[0] : null,
            removeItemClickHandler: this.removeItemFromCartHandler,
            moveToWishlistSuccessHandler: this.moveToWishlistSuccessHandler,
            waitingComponent: <Waiting className='msc-waiting-circular msc-waiting-lg' />,
            cartLoadingStatus: this.props.data.cart.status === 'FAILED' ? this.props.resources.cartLoadingErrorMessage : null,
            cartDataResult: this.props.data.cart.result && this.props.data.cart.result.isEmpty,
            OrderSummaryErrors: this._getOrderSummaryErrors(cartLines),
            telemetryContent: this.telemetryContent,
            unitConversions: this.state.unitConversions,
            activePrices: this.state.activePrices,
            breakoffProducts: this.state.breakoffProducts,
            onBreakoffChange: this._onBreakoffChange,
            selectedProducts: this.state.selectedProducts,
            onUpdateSelection: this._onUpdateSelection,
            updateCartlineQuantity: this.updateCartlineQuantity,
            onUpdateOrderTemplates: this._onUpdateOrderTemplates,
            retrieveOrderTemplates: this._retrieveOrderTemplates,
            isUpdatingQuantity: this.state.isUpdatingQuantity
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }
    /* eslint-enable complexity -- Disabling complexity for render method */

    /**
     * Promotion click event handler.
     * @param event - The mouse event.
     */
    private readonly promotionLinkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        this.setState({
            showPromotionDialog: !this.state.showPromotionDialog
        });
    };

    private _isAnonymousCheckoutEnabled(): boolean {
        const { config, data } = this.props;
        const cartConfiguration = data.cartConfiguration?.result || {};
        const allowAnonymousCheckout = cartConfiguration?.AllowAnonymousCheckout;

        return isAnonymousCheckoutEnabled(allowAnonymousCheckout, config.showGuestCheckoutButton);
    }

    /**
     * Promotion close button click.
     */
    private readonly promotionCloseButtonclick = () => {
        this._hidePromotionDialog();
    };

    /**
     * Hide promotion dialog.
     */
    private _hidePromotionDialog(): void {
        this.setState({
            showPromotionDialog: false
        });
    }

    /**
     * Display add to cart template.
     * @returns True if should display, false otherwise.
     */
    private _displayAddCartToTemplate(): boolean {
        const cart = this.props.data.cart?.result;
        if (ObjectExtensions.isNullOrUndefined(cart) || cart.hasInvoiceLine) {
            return false;
        }

        if (
            isChannelTypeB2B(this.props.context.actionContext.requestContext) &&
            this.props.data.orderTemplates.result &&
            ArrayExtensions.hasElements(cart.cart?.CartLines) &&
            this.props.data.customerInformation.result
        ) {
            return true;
        }

        return false;
    }

    /**
     * Get promotions.
     * @returns React node with the promotions.
     */
    private _getPromotions(): React.ReactNode {
        return (
            this.props.config.showAvailablePromotions &&
            cartPromotions({
                id: this.promotionPopupId,
                showPromotionPopup: this.state.showPromotionDialog,
                onSeeAllClick: this.promotionLinkClick,
                onCloseButtonClick: this.promotionCloseButtonclick,
                promotions: this.state.promotionData.promotionOptions,
                resources: {
                    promotionStringHeading: this.props.resources.promotionStringHeading,
                    promotionLinkText: this.props.resources.promotionLinkText,
                    promotionPopupHeading: this.props.resources.promotionPopupHeading,
                    promotionCloseButtonAriaLabel: this.props.resources.promotionCloseButtonAriaLabel
                }
            })
        );
    }

    private async _getActivePrices(): Promise<void> {
        const { actionContext, request } = this.props.context;

        if (!request.user.isAuthenticated) {
            return;
        }

        const cartLines = (await this.props.data.cart).cart.CartLines;
        const ids = cartLines?.map(x => x.ProductId).filter(Boolean) as number[] | undefined;

        if (!ids || ids.length === 0) {
            return;
        }

        const projectDomain: ProjectionDomain = { ChannelId: +request.apiSettings.channelId, CatalogId: request.apiSettings.catalogId };
        const activePrices = await getActivePricesAsync({ callerContext: actionContext },
            projectDomain,
            ArrayExtensions.unique(ids),
            new Date(),
            null,
            null,
            true,
            false,
            false);

        this.setState({ activePrices });
    }

    private async _getUnitConversions(): Promise<void> {
        const { actionContext, request, app } = this.props.context;

        if (!request.user.isAuthenticated || app.config.unitOfMeasureConversions !== true) {
            return;
        }

        const cartLines = (await this.props.data.cart).cart.CartLines;
        const ids = cartLines?.map(x => x.ProductId).filter(Boolean) as number[] | undefined;

        if (!ids || ids.length === 0) {
            return;
        }

        const unitConversions = await getProductUnitOfMeasureOptionsAsync({ callerContext: actionContext }, ids);

        this.setState({ unitConversions });
    }

    private async _recalculateCartPrices(): Promise<void> {
        const { config, context, data } = this.props;
        const { cookies } = context.request;
        const cart = await data.cart;
        const firstAddress = data.address.result?.[0];
        const lastCartVersion = parseFloat(cookies.get('last_cart_version').value as string);

        if (cart.cart.Version === lastCartVersion) {
            if (MsDyn365.isBrowser) {
                // Used by InstaPunchout to make sure VAT is calculated before proceeding.
                // Removing this WILL break VAT for InstaPunchout customers.
                window.products = data.products.result || [];
            }
            return;
        }

        const newCartObject: CartType = {
            Id: cart.cart.Id,
            ExtensionProperties: [
                {
                    Key: 'LastCalculatedDate',
                    Value: { DateTimeOffsetValue: new Date() }
                }
            ]
        };

        if (firstAddress && config.enableTaxCalculation) {
            newCartObject.ShippingAddress = firstAddress;

            const shipCarrierAddress = await getShipCarrierAddressAsync(context.actionContext, firstAddress.LogisticsLocationRecordId || 0);
            const orgDelivery = this.props.data.organizationInformation?.result?.ExtensionProperties?.find(
                property => property.Key === 'DLVMODE'
            )?.Value?.StringValue;

            newCartObject.DeliveryMode = shipCarrierAddress?.DlvModeId || orgDelivery || 'Levering';
        } else if (!config.enableTaxCalculation && (cart.cart.ShippingAddress || cart.cart.DeliveryMode)) {
            newCartObject.ShippingAddress = null as any;
            newCartObject.DeliveryMode = null as any;
        }

        await cart.updateCart({ newCartObject });

        // Update VAT on products
        const itemTaxGroupMapping: Record<string, string> = {
            'F': '0%',
            'H': '25%',
            'M': '15%',
            'L': '12%'
        };
        for (const product of data.products.result || []) {
            const cartLine = cart.cart.CartLines?.find(line => line.ProductId === product.RecordId);
            product.VAT = cartLine?.ItemTaxGroupId ? itemTaxGroupMapping[cartLine.ItemTaxGroupId] : undefined;
        }
        this.props.context.actionContext.update(new ActiveCartProductsInput(), data.products.result || []);

        if (MsDyn365.isBrowser) {
            // Used by InstaPunchout to make sure VAT is calculated before proceeding.
            // Removing this WILL break VAT for InstaPunchout customers.
            window.products = data.products.result || [];
        }

        cookies.set('last_cart_version', cart.cart.Version);
    }

    private searchBarOnChange = debounce((searchTerm: string) => {
        this.setState({ searchTerm });
    }, 500);

    private _getSearchBar(): React.ReactNode {
        const { data, resources } = this.props;
        const cart = data.cart.result;
        const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;

        if (isCartEmpty) {
            return null;
        }

        return (
            <div className='msc-search-bar-container'>
                <svg className='msc-search-bar__icon' width="1200pt" height="1200pt" version="1.1" viewBox="0 0 1200 1200" xmlns="http://www.w3.org/2000/svg">
                    <path d="m804 504c0 165.71-134.29 300-300 300s-300-134.29-300-300 134.29-300 300-300 300 134.29 300 300zm-53.766 294.66c-66.656 55.781-152.53 89.344-246.24 89.344-212.06 0-384-171.94-384-384s171.94-384 384-384 384 171.94 384 384c0 88.078-29.672 169.26-79.547 234.05l253.87 264.89c16.031 16.734 15.469 43.359-1.2656 59.391-16.734 16.031-43.359 15.469-59.391-1.2656z" fillRule="evenodd"/>
                </svg>
                <input
                    className='msc-search-bar'
                    placeholder={resources.searchPlaceholderText}
                    onChange={e => this.searchBarOnChange(e.target.value)}
                />
            </div>
        );
    }

    /**
     * Add error message in cart line in case the quantity exceeds the current inventory.
     * @param cartLines - The cart lines.
     * @param cartLineInventoryValidationResult - The cart line inventory result.
     */
    private _updateCartLineErrorForInvalidInventory(
        cartLines: ICartlinesViewProps[] | null,
        cartLineInventoryValidationResult: ICartLineInventoryValidationResult
    ) {
        const { resources } = this.props;

        if (
            ArrayExtensions.hasElements(cartLines) &&
            !cartLineInventoryValidationResult.isValid &&
            ArrayExtensions.hasElements(cartLineInventoryValidationResult.productIdWithErrors)
        ) {
            for (const productIdWithError of cartLineInventoryValidationResult.productIdWithErrors) {
                const cartLine = cartLines?.find(element => element.data?.cartline.ProductId === productIdWithError);
                if (cartLine) {
                    cartLine.error = resources.outOfStockErrorMessage;
                    cartLine.hasError = true;
                }
            }
        }
    }

    /**
     * Get order summary errors.
     * @param cartLines - The cart lines.
     * @returns Order summary errors.
     */
    private _getOrderSummaryErrors(cartLines: ICartlinesViewProps[] | null): IOrderSummaryErrors | undefined {
        /* Commented code here is used for aggregating only special orders.
         * Right now all errors are aggregated regardless of the product type.
         */

        // let hasSpecialOrders: boolean = false;

        let allErrors =
            (cartLines &&
                cartLines
                    .map((cartline: ICartlinesViewProps, index: number) => {
                        // Check if the product is service or not by product type
                        const isServiceItem = cartline.data?.product?.ItemTypeValue === ReleasedProductType.Service;

                        if (!isServiceItem && cartline.error) {
                            const fullError =
                                cartline.data && cartline.data.product?.Name
                                    ? `${cartline.data.product.Name} ${cartline.error}`
                                    : cartline.error;

                            return (
                                <div key={cartline.cartlineId} className='msc-alert__line'>
                                    {fullError}
                                </div>
                            );
                        }

                        return null;
                    })
                    .filter((line): line is JSX.Element => !!line)) ||
            [];

        if (allErrors.length > 0) {
            allErrors = [(
                <div key='specialOrdersWarning' className='msc-alert__line'>
                    {this.props.resources.specialOrdersWarning}
                </div>
            )];
            /* allErrors.splice(0, 0, (
                <div key='specialOrdersWarning' className='msc-alert__line'>
                    {this.props.resources.specialOrdersWarning}
                </div>
            )); */
        }

        return {
            Wrapper: {
                tag: Alert,
                className: 'msc-alert',
                isOpen: allErrors.length > 0,
                color: 'danger',
                assertive: true,
                'aria-label': this.props.resources.outOfStockErrorMessageHeader
            },
            header: (
                <div className='msc-alert__header' aria-hidden='true'>
                    <span className='msi-exclamation-triangle' />
                    <span>{this.props.resources.outOfStockErrorMessageHeader}</span>
                </div>
            ),
            errors: allErrors
        };
    }

    /**
     * Gets invoice summary line items.
     * @returns Invoice summary lines.
     */
    private _getInvoiceSummaryLineItems(): IInvoiceSummaryLines | undefined {
        const {
            data: {
                cart: { result: cart }
            },
            telemetry,
            resources
        } = this.props;

        return cart?.hasInvoiceLine
            ? InvoiceSummary({
                  orderTotalLabel: resources.totalAmountLabel,
                  invoiceLabel: resources.invoiceLabel,
                  cart: cart.cart,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  telemetry,
                  id: this.props.id
              })
            : undefined;
    }

    /**
     * Gets order summary line items.
     * @returns Order summary lines.
     */
    private _getOrderSummaryLineItems(): IOrderSummaryLinesExt | undefined {
        const {
            data: {
                cart: { result: cart },
                channelDeliveryOptionConfig
            },
            telemetry,
            resources
        } = this.props;

        const channelConfiguration = this.props.context && this.props.context.request && this.props.context.request.channel;
        const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
        const featureState = this.props?.data?.featureState;
        const retailMultiplePickUpOptionEnabled = featureState?.result?.find(item => item.Name === multiplePickupStoreSwitchName)
            ?.IsEnabled;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- app configs are of generic type
        const isShowOrHideTaxEnabled = this.props.context.request.app.config?.isEnableShowOrHideSalesTaxECommerceEnabled === true;
        const fixedCart = { ...cart!.cart };

        fixedCart.OtherChargeAmount = undefined;

        const lines: IOrderSummaryLinesExt | undefined = channelConfiguration && ArrayExtensions.hasElements(cart?.cart.CartLines) && !cart?.hasInvoiceLine
            ? OrderSummary({
                cart: fixedCart,
                subTotalLabel: resources.itemsWithCountLabel,
                shippingLabel: resources.estimatedShippingLabel,
                otherChargeLabel: resources.otherCharges,
                taxLabel: resources.estimatedTaxLabel,
                orderTotalLabel: resources.orderTotal,
                totalDiscountsLabel: resources.totalDiscountsLabel,
                context: this.props.context,
                typeName: this.props.typeName,
                id: this.props.id,
                toBeCalculatedText: resources.yetToBeCalculatedText,
                /* freeText: resources.priceFree, */
                channelConfiguration,
                retailMultiplePickUpOptionEnabled,
                channelDeliveryOptionConfig: channelDeliveryOptionConfig?.result,
                telemetry,
                isTaxIncludedInPrice: cart!.cart.IsTaxIncludedInPrice,
                isShowTaxBreakup: isShowOrHideTaxEnabled
            })
            : undefined;

        const charges = getAllCharges(fixedCart);
        const extraLines: JSX.Element[] = [];
        charges?.forEach(charge => {
            if (lines?.subtotal) {
                extraLines.push(React.cloneElement(lines.subtotal as JSX.Element, {
                    cssLabel: `extra ${charge.ChargeCode.toLowerCase().replace(/ /g, '-')}`,
                    label: charge.Description,
                    price: charge.TotalCalculatedAmount
                }));
            }
        });

        if (lines && extraLines) {
            lines.charges = extraLines;
        }

        return lines;
    }

    private _renderActionBar(): React.ReactNode {
        const { config, context, resources } = this.props;
        const { selectedProducts } = this.state;

        if (!context.request.user.isAuthenticated) {
            return null;
        }

        // Check if actions are enabled and export url is valid
        if (!config.enableCartExportImport || !config.exportCartLink || !config.exportCartLink.linkUrl.destinationUrl) {
            return null;
        }

        const exportCartLink = config.exportCartLink;

        const emptyCart = async () => {
            const { emptyCartConfirmationTitle, emptyCartConfirmationMessage } = resources;
            const confirm = await ConfirmationService.confirm(emptyCartConfirmationTitle, emptyCartConfirmationMessage);

            if (!confirm) {
                return;
            }

            const cart = await this.props.data.cart;

            // For GTM. Get each line id and quantity and store as Record<string, number>
            const products = this.props.data.products.result?.filter(p => {
                return p.RecordId && cart.cart.CartLines?.some(cartLine => cartLine.ProductId === p.RecordId);
            });
            const cartLinesToRemove: Record<string, number> = {};
            cart.cart.CartLines?.forEach(cartLine => {
                if (cartLine.ProductId && cartLine.Quantity) {
                    cartLinesToRemove[cartLine.ProductId] = cartLine.Quantity;
                }
            });

            await cart.removeCartLines({ cartLineIds: cart.cart.CartLines!.map(x => x.LineId!) });

            if (products) {
                const productsWithPrice = [ ...products ];
                productsWithPrice.forEach(product => {
                    const price = this.state.activePrices.find(p => p.ProductId === product.RecordId);
                    product.Price = price?.CustomerContextualPrice || product.Price;
                });
                void GTM.removeProductsFromCartAsync(products, cartLinesToRemove);
            }
        };
        const removeAllSelectedCarts = async () => {
            const { deleteLinesConfirmationTitle, deleteLinesConfirmationMessage } = resources;

            const confirm = await ConfirmationService.confirm(
                format(deleteLinesConfirmationTitle, Object.values(selectedProducts || {}).filter(Boolean).length), deleteLinesConfirmationMessage);

            if (!confirm) {
                return;
            }

            try {
                const cartState = await this.props.data.cart;
                const lineItemIdsToRemove: string[] = [];
                cartState.cart.CartLines?.forEach(cartLine => {
                    if (cartLine.LineId && cartLine.ProductId && selectedProducts?.[cartLine.ProductId]) {
                        delete selectedProducts[cartLine.ProductId];
                        lineItemIdsToRemove.push(cartLine.LineId);
                    }
                });
                if (lineItemIdsToRemove.length > 0) {
                    await cartState.removeCartLines({ cartLineIds: lineItemIdsToRemove });
                }
            } catch (error) {
                throw error;
            }
        };

        const featureState = this.props.data.featureState;
        const channelMultipleCatalogsFeature = 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature';
        const isMultipleCatalogsFeatureEnabled =
            featureState.result?.find((item: FeatureState) => item.Name === channelMultipleCatalogsFeature)?.IsEnabled || false;
        const currentCatalogId = getCatalogId(this.props.context.request);
        validateCatalogId(currentCatalogId);

        // We can't add <svg> to the SaveCartToOrderTemplateComponent, so we'll define it as a CSS variable and use it in the CSS
        const customCSSVariables: React.CSSProperties = {
            // @ts-expect-error
            '--addcarttotemplate-icon': `url(${getAsset('icons/circle-plus.svg', this.props.context.request)})`
        };

        return (
            <div className='cart-view-page__action-bar' style={customCSSVariables}>
                {this._displayAddCartToTemplate() && this.props.data.products.result ? (
                    <SaveCartToOrderTemplateComponent
                        addToOrderTemplateDialogResources={this._generateAddToOrderTemplateDialogResources(resources)}
                        createOrderTemplateDialogResources={this._generateOrderTemplateNameDialogResources(resources)}
                        linesAddedToOrderTemplateDialogResources={this._generateLinesAddedToOrderTemplateDialogResources(resources)}
                        duplicateItemsWhenAddingToOrderTemplateDialogResources={this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                            resources
                        )}
                        createOrderTemplateFromCartButton={resources.createOrderTemplateFromCartButton}
                        createOrderTemplateFromCartButtonTooltip={resources.createOrderTemplateFromCartButtonTooltip}
                        context={this.props.context}
                        id={this.props.id}
                        typeName={this.props.typeName}
                        isMultipleCatalogFeatureEnabledInHq={isMultipleCatalogsFeatureEnabled || false}
                        catalogId={currentCatalogId}
                        data={{
                            orderTemplates: this.props.data.orderTemplates.result!,
                            cartLines: this.props.data.cart.result!.cart.CartLines!,
                            customerInformation: this.props.data.customerInformation.result
                        }}
                    />
                ) : (
                    <Button className='msc-cart__btn-addcarttotemplate msc-btn' disabled title={resources.createOrderTemplateFromCartButton}>
                        {resources.createOrderTemplateFromCartButton}
                    </Button>
                )}
                <Button
                    className='ms-cart__list-buttons__remove'
                    disabled={Object.values(selectedProducts || {}).filter(Boolean).length === 0}
                    onClick={removeAllSelectedCarts}
                >
                    <img src={getAsset('icons/trash.svg', this.props.context.request)} width={21} />
                    {resources.removeSelectedToCartText}
                </Button>
                <Button className='cart-view-page__action-bar__import' onClick={() => this.setState({ showImportModal: true })}>
                    <img src={getAsset('icons/import.svg', this.props.context.request)} width={21} />
                    {resources.importCartButtonText}
                </Button>
                <a
                    className='cart-view-page__action-bar__export'
                    href={exportCartLink.linkUrl.destinationUrl}
                    aria-label={exportCartLink.ariaLabel}
                    target={exportCartLink.openInNewTab ? '_blank' : undefined}
                    rel={exportCartLink.openInNewTab ? 'noopener noreferrer' : undefined}
                >
                    <img src={getAsset('icons/export.svg', this.props.context.request)} width={21} />
                    {resources.exportCartButtonText}
                </a>
                <Button className='ms-cart__list-buttons__empty-cart' onClick={emptyCart}>
                    <img src={getAsset('icons/trash.svg', this.props.context.request)} width={21} />
                    {resources.emptyCartButtonText}
                </Button>
                {this.state.isRecalculatingCartPrices && (
                    <div className='cart-view-page__action-bar__calculating'>
                        <Waiting />
                        <span className='cart-view-page__action-bar__calculating__text'>{resources.recalculatingText}...</span>
                    </div>
                )}
            </div>
        );
    }

    private _renderImportModal(): React.ReactNode {
        const { showImportModal } = this.state;

        if (!showImportModal) {
            return null;
        }

        const onToggle = () => {
            this.setState({ showImportModal: !showImportModal });
        };

        return (
            <ImportModal {...this.props} onToggle={onToggle} />
        );
    }

    /**
     * Gets promo code.
     * @returns React node with the promo code.
     */
    private _getPromoCode(): React.ReactNode {
        const {
            data: {
                cart: { result: cart }
            },
            resources
        } = this.props;

        return (
            cart && (
                <PromoCodeComponent
                    cart={cart}
                    promoCodeHeadingText={resources.promoCodeHeadingText}
                    promoPlaceholderText={resources.promoPlaceholderText}
                    promoCodeApplyButtonText={''/* resources.promoCodeApplyButtonText */}
                    appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
                    collapseTimeOut={350}
                    removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
                    removePromoText={resources.removePromoText}
                    invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
                    failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
                    failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
                    duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
                    context={this.props.context}
                    id={this.props.id}
                    typeName={this.props.typeName}
                    data={{}}
                    telemetryContent={this.telemetryContent}
                />
            )
        );
    }

    /**
     * Generate add to order template dialog resources.
     * @param resources - The resources.
     * @returns IAddToOrderTemplateDialogResources.
     */
    private _generateAddToOrderTemplateDialogResources(resources: IDlvryCartResources): IAddToOrderTemplateDialogResources {
        return {
            addToOrderTemplateHeader: resources.addToOrderTemplateHeader,

            noOrderTemplatesMessage: resources.noOrderTemplatesMessage,
            noOrderTemplatesDescription: resources.noOrderTemplatesDescription,

            createAnOrderTemplateButtonText: resources.createAnOrderTemplateButtonText,
            createNewOrderTemplateButtonText: resources.createNewOrderTemplateButtonText,
            cancelOrderTemplateCreationButtonText: resources.cancelOrderTemplateCreationButtonText,

            selectTemplatesText: resources.selectTemplatesText,
            addToTemplateButtonText: resources.addToTemplateButtonText,
            lineItemsText: resources.lineItemsText
        };
    }

    /**
     * Generate item added to order template dialog resources.
     * @param resources - The resources.
     * @returns IItemAddedToOrderTemplateDialogResources.
     */
    private _generateItemAddedToOrderTemplateDialogResources(resources: IDlvryCartResources): IItemAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            itemAddedToOrderTemplateHeaderItemOneText: resources.itemAddedToOrderTemplateHeaderItemOneText,
            itemAddedToOrderTemplateHeaderItemFormatText: resources.itemAddedToOrderTemplateHeaderItemFormatText,
            itemAddedToOrderTemplateHeaderMessageText: resources.itemAddedToOrderTemplateHeaderMessageText,
            freePriceText: resources.freePriceText,
            originalPriceText: resources.originalPriceText,
            currentPriceText: resources.currentPriceText
        };
    }

    /**
     * Generate lines added to order template dialog resources.
     * @param resources - The resources.
     * @returns ILinesAddedToOrderTemplateDialogResources.
     */
    private _generateLinesAddedToOrderTemplateDialogResources(resources: IDlvryCartResources): ILinesAddedToOrderTemplateDialogResources {
        return {
            viewOrderTemplateButtonText: resources.viewOrderTemplateButtonText,
            continueShoppingButtonText: resources.continueShoppingButtonText,
            linesAddedToOrderTemplateHeaderItemsOneText: resources.linesAddedToOrderTemplateHeaderItemsOneText,
            linesAddedToOrderTemplateHeaderItemsFormatText: resources.linesAddedToOrderTemplateHeaderItemsFormatText,
            linesAddedToOrderTemplateHeaderLinesOneText: resources.linesAddedToOrderTemplateHeaderLinesOneText,
            linesAddedToOrderTemplateHeaderLinesFormatText: resources.linesAddedToOrderTemplateHeaderLinesFormatText,
            linesAddedToOrderTemplateHeaderMessageText: resources.linesAddedToOrderTemplateHeaderMessageText,
            linesAddedToOrderTemplateCustomPriceAlertMessageText: resources.linesAddedToOrderTemplateCustomPriceAlertMessageText
        };
    }

    /**
     * Generate duplicate items when adding to order template dialog resources.
     * @param resources - The resources.
     * @returns IDuplicateItemsWhenAddingToOrderTemplateDialogResources.
     */
    private _generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
        resources: IDlvryCartResources
    ): IDuplicateItemsWhenAddingToOrderTemplateDialogResources {
        return {
            duplicatedProductsHeader: resources.duplicatedProductsHeader,
            duplicatedProductsDescription: resources.duplicatedProductsDescription,
            updateQuantityButtonText: resources.updateQuantityButtonText,
            cancelDuplicateItemsButtonText: resources.cancelDuplicateItemsButtonText
        };
    }

    /**
     * Generate order template name dialog resources.
     * @param resources - The resources.
     * @returns IOrderTemplateNameDialogResources.
     */
    private _generateOrderTemplateNameDialogResources(resources: IDlvryCartResources): IOrderTemplateNameDialogResources {
        return {
            orderTemplateHeaderLabel: resources.createOrderTemplateHeader,
            orderTemplateTitleLabel: resources.orderTemplateTitle,
            orderTemplateNameAriaLabel: resources.orderTemplateNameAriaLabel,
            orderTemplateDescription: resources.createOrderTemplateDescription,

            defaultOrderTemplateName: resources.defaultOrderTemplateName,

            orderTemplateButtonLabel: resources.createOrderTemplateButtonText,
            orderTemplateCancelButtonLabel: resources.cancelNewOrderTemplateCreationButtonText
        };
    }

    /**
     * Gets the cart lines view props.
     * @returns ICartlinesViewProps [].
     */
    private _getCartLines(): ICartlinesViewProps[] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this.getImageSettings(config.imageSettings);

        const RetailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );
        const multiplePickUpEnabled = RetailMultiplePickupFeatureState?.IsEnabled;

        const cartlinesErrors: CartLineValidationResultsByLineId = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }
        const channelMultipleCatalogsFeature = 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature';
        const isMultipleCatalogsFeatureEnabled =
            this.props.data.featureState.result?.find((item: FeatureState) => item.Name === channelMultipleCatalogsFeature)?.IsEnabled ||
            false;

        this.props.data.products.result?.forEach(x => {
            const price = this.state.activePrices.find(y => y.ProductId === x.RecordId);
            if (price) {
                x.Badge = getDiscountBadge(price, x.FallbackBadge);
                x.ProductPrice = price;
            }
        });

        return ArrayExtensions.hasElements(cart?.cart.CartLines)
            ? CartLineItems({
                  cartlines: cart!.cart.CartLines,
                  storeSelectorModuleId: storeSelectorId,
                  cartlinesErrors,
                  products: this.props.data.products.result,
                  productAvailabilites: this._getProductAvailabilities(),
                  productDeliveryOptions: undefined,
                  catalogs: this.props.data.catalogs?.result,
                  imageSettings,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  maxCartlineQuantity: updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem),
                  shouldIgnoreWarehouse: this.props.context.app.config.warehouseAggregation === 'multiple',
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  retailMulitplePickupFeatureState: multiplePickUpEnabled,
                  resources: {
                      sizeString: resources.productDimensionTypeSize,
                      colorString: resources.productDimensionTypeColor,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      amountString: resources.productDimensionTypeAmount,
                      quantityDisplayString: resources.quantityDisplayText,
                      inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                      decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                      incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                      discountStringText: resources.discountStringText,
                      originalPriceText: resources.originalPriceText,
                      currentPriceText: resources.currentPriceText,
                      shippingChargesText: resources.shippingCharges,
                      salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                      priceEditorResources: {
                          ...resources
                      },
                      depositSuffixText: resources.depositSuffixText
                  },
                  telemetry,
                  addToWishlistButtonText: resources.addToWishlistButtonText,
                  removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                  removeButtonText: resources.removeCartButtonText,
                  shipItText: resources.shipInsteadDisplayText,
                  pickitUpText: resources.pickItUpDisplayText,
                  changeStoreText: resources.changeStoreDisplayText,
                  storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                  outOfStockText: resources.outOfStockErrorMessage,
                  outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                  outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                  cartState: this.props.data.cart.result,
                  orgUnitLocations: this.props.data.orgUnitLocations.result,
                  removeItemClickHandler: this.removeItemFromCartHandler,
                  moveToWishlistSuccessHandler: this.moveToWishlistSuccessHandler,
                  updateCartLinesQuantitySuccessHandler: this.updateCartlineQuantity,
                  locationChangedHandler: this.fulfillmentStoreIdUpdated,
                  wishlists,
                  defaultWishlistName: resources.defaultWishlistName,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  includeErrors: config.showOutOfStockErrors,
                  showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                  telemetryContent: this.telemetryContent,
                  isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                  addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                  createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                  itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                  duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                      resources
                  ),

                  addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                  addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                  addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                  addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                  orderTemplates,
                  customerInformation,
                  productAvailability: this.props.data.productAvailabilites,
                  isMultipleCatalogFeatureEnabledInHq: isMultipleCatalogsFeatureEnabled || false,
                  unitConversions: this.state.unitConversions
              })
            : null;
    }

    /**
     * Gets the checkout URL.
     * @param isAuthenticated - Is authenticated flag.
     * @param guestCheckoutUrl - Guest checkout URL.
     * @param signInUrl -The sign in URL.
     * @returns The checkout URL.
     */
    private getCheckoutURL(isAuthenticated: boolean, guestCheckoutUrl: string, signInUrl: string): string {
        if (isAuthenticated) {
            return guestCheckoutUrl;
        }

        const isAbsoluteUrl = urlCheck(guestCheckoutUrl);
        let returnURL = guestCheckoutUrl;
        if (MsDyn365.isBrowser && !isAbsoluteUrl) {
            returnURL = `${window.location.origin}${guestCheckoutUrl}`;
        }

        return `${signInUrl}${!signInUrl.includes('?') ? '?' : '&'}ru=${returnURL}`;
    }

    /**
     * Gets the image settings.
     * @param imageSettings - Image settings.
     * @returns IImageSettings.
     */
    private readonly getImageSettings = (imageSettings: IImageSettings | undefined): IImageSettings => {
        const viewPorts: Partial<IImageSettings> = {
            viewports: {
                xs: { q: 'w=183&h=183&m=0', w: 0, h: 0 },
                sm: { q: 'w=183&h=183&m=0', w: 0, h: 0 },
                lg: { q: 'w=183&h=183&m=0', w: 0, h: 0 },
                md: { q: 'w=183&h=183&m=0', w: 0, h: 0 },
                xl: { q: 'w=183&h=183&m=0', w: 0, h: 0 }
            }
        };

        if (imageSettings) {
            return { ...imageSettings, ...viewPorts };
        }

        return {
            ...viewPorts,
            lazyload: true
        } as IImageSettings;
    };

    /**
     * Remove item from dlvry-cart handler.
     * @param cartlineToRemove - The cart line to remove.
     */
    private readonly removeItemFromCartHandler = (cartlineToRemove: CartLine) => {
        if (this.props.data.cart.result) {
            const input = {
                cartLineIds: [cartlineToRemove.LineId!.toString()]
            };
            this.props.data.cart.result
                .removeCartLines(input)
                .then(() => {
                    const product = this.props.data.products.result?.find(
                        product => product.RecordId === cartlineToRemove.ProductId
                    );
                    if (product) {
                        const productWithPrice: SimpleProductExt = {
                            ...product,
                            Price: this.state.activePrices.find(
                                price => price.ProductId === product.RecordId
                            )?.CustomerContextualPrice || product.Price || 0
                        };
                        void GTM.removeFromCartAsync(productWithPrice, cartlineToRemove.Quantity || 1);
                    }

                    this.validateCart().catch(error => {
                        this.props.telemetry.warning(error);
                        this.props.telemetry.debug('Unable to validate Cart');
                    });
                })
                .catch(error => {
                    this.props.telemetry.warning(error);
                    this.props.telemetry.debug('Unable to Remove Cart Line');
                });
        }
    };

    /**
     * Move to wish list handler.
     * @param result - The wish list action success result.
     * @param cartline - The cart line.
     */
    private readonly moveToWishlistSuccessHandler = (result: IWishlistActionSuccessResult, cartline?: CartLine) => {
        const DISABLED = true;

        if (DISABLED) {
            return;
        }

        if (result.status === 'ADDED' && cartline) {
            this.removeItemFromCartHandler(cartline);
        }
    };

    private _updateCartLines: Record<string, number> = {};

    /**
     * Updates the cart line quantity.
     * @param cartlineToUpdate - The cart line.
     * @param quantity - The quantity.
     * @param lineIndex - The line index.
     * @returns True if the cart line was updated, false otherwise.
     */
    private readonly updateCartlineQuantity = (cartlineToUpdate: CartLine, quantity: number, lineIndex?: number): boolean => {
        this._updateCartLines[cartlineToUpdate.LineId!] = quantity;

        this.cartLineQuantityUpdatedAsyncDebounced(
            this.isQuantityLimitsFeatureEnabled,
            this.props.data.cart.result,
            this.props.telemetry
        )
            ?.then((cartActionResult: ICartActionResult | undefined): boolean => {
                if (cartActionResult?.status === 'SUCCESS') {
                    this.validateCart().catch(error => {
                        this.props.telemetry.warning((error as Error).message);
                        this.props.telemetry.debug('Unable to validate Cart');
                    });
                    this.setState({
                        cartlinesErrors: {}
                    });
                    return true;
                }

                if (!ObjectExtensions.isNullOrUndefined(cartActionResult)) {
                    // Show update line error message.
                    const lineUpdateFailure: LineValidationResult = {
                        LineId: ObjectExtensions.isNullOrUndefined(cartlineToUpdate.LineId) ? '0' : cartlineToUpdate.LineId,
                        ValidationFailures: [
                            {
                                ErrorContext: ArrayExtensions.hasElements(cartActionResult.validationResults)
                                    ? cartActionResult.validationResults[0].ErrorContext
                                    : '',
                                LocalizedMessage: cartActionResult.errorDetails?.LocalizedMessage
                            }
                        ]
                    };

                    this.setState({
                        cartlinesErrors: { ValidationResultsPairs: [lineUpdateFailure] }
                    });
                }

                /*
                if (!ObjectExtensions.isNullOrUndefined(cartActionResult)) {
                    // Show update line error message.
                    const lineUpdateFailure: LineDataValidationFailure = {
                        LineIndex: ObjectExtensions.isNullOrUndefined(lineIndex) ? 0 : lineIndex,
                        DataValidationFailure: {
                            ErrorContext: ArrayExtensions.hasElements(cartActionResult.validationResults)
                                ? cartActionResult.validationResults[0].ErrorContext
                                : '',
                            LocalizedMessage: cartActionResult.errorDetails?.LocalizedMessage
                        }
                    };

                    this.setState((previousState: Readonly<ICartComponentState>) => {
                        const currentErrors: LineDataValidationFailure[] = previousState.cartlinesErrors.filter(
                            error => error.LineIndex !== lineIndex
                        );
                        currentErrors.push(lineUpdateFailure);
                        return { cartlinesErrors: currentErrors };
                    });
                }
                */

                return false;
            })
            .catch((error) => {
                return false;
            });

        return false;
    };

    private async updateCartLineQuantityAsync(
        isQuantityLimitsFeatureEnabled: boolean | undefined,
        cartResult: ICartState | undefined,
        telemetry: ITelemetry
    ): Promise<ICartActionResult | undefined> {
        // const result = await cartLineQuantityUpdatedAsync(isQuantityLimitsFeatureEnabled, cartResult, telemetry, products, cartLineToUpdate, quantity);

        const updateCartLines = { ...this._updateCartLines };

        try {
            this.setState({ isUpdatingQuantity: true });

            const cartLinesToUpdate: CartLine[] = Object.keys(updateCartLines)
                .map(lineId => ({
                    LineId: lineId,
                    Quantity: updateCartLines[lineId]
                }))
                .filter(cartLine => {
                    const lineInCart = cartResult?.cart.CartLines?.find(x => x.LineId === cartLine.LineId);
                    return !lineInCart || lineInCart.Quantity !== cartLine.Quantity;
                });

            if (cartLinesToUpdate.length > 0) {
                await updateCalcCartLinesAsync(
                    { callerContext: this.props.context.actionContext },
                    cartResult!.cart.Id,
                    cartLinesToUpdate,
                    CalculationModes.All,
                    null
                );

                if (cartResult?.cart && this.props.data.products.result) {
                    void GTM.updateCartAsync(cartResult.cart, this._updateCartLines, this.props.data.products.result, this.state.activePrices);
                }

                this._updateCartLines = {};
            }

            const result = await cartResult?.refreshCart({});

            this.props.context.request.cookies.set('last_cart_version', (await this.props.data.cart).cart.Version);

            return result;
        } catch (error) {
            toasts.error(this.props.resources.toastErrorHeader || 'Error', (error as Error)?.message || 'Unknown error');
        } finally {
            this.setState({ isUpdatingQuantity: false });
        }

        return undefined;
    }

    /**
     * Fulfillment store id is updated.
     */
    private readonly fulfillmentStoreIdUpdated = () => {
        if (this.isQuantityLimitsFeatureEnabled) {
            // Refresh products (in props.data) based on latest cart.
            getActiveCartProductsAction(new ActiveCartProductsInput(), this.props.context.actionContext)
                .then(async () => this.validateCart())
                .catch((error: Error) => {
                    this.props.telemetry.warning(error.message);
                    this.props.telemetry.debug('Unable to validate Cart');
                });
        }
    };

    /**
     * Validates cart.
     * @returns Void promise.
     */
    private async validateCart(): Promise<void> {
        if (!this.isQuantityLimitsFeatureEnabled) {
            this.setState({ cartlinesErrors: {}, isCartValid: true });
            return AsyncResult.resolve();
        }

        const {
            data: { cart },
            telemetry,
            context
        } = this.props;

        const validationResults: CartLineValidationResultsByLineId | undefined = await validateCartAsync(cart, telemetry, context, true);

        if (validationResults) {
            validationResults.ValidationResultsPairs?.forEach(validationResult => {
                // Ignore error messages for the multiplicity of configured number.
                if (validationResult.ValidationFailures) {
                    validationResult.ValidationFailures = validationResult.ValidationFailures.filter(y =>
                        y.ErrorResourceId !== 'Microsoft_Dynamics_Commerce_Runtime_ItemQuantityViolatesMultiplicityOfConfiguredNumber'
                    );
                }
            });

            validationResults.ValidationResultsPairs = validationResults?.ValidationResultsPairs?.filter(x =>
                x.ValidationFailures && x.ValidationFailures.length > 0
            );
        }

        if (validationResults) {
            const isValid = !ArrayExtensions.hasElements(validationResults.ValidationResultsPairs);
            this.setState({
                cartlinesErrors: validationResults,
                isCartValid: isValid
            });
        } else {
            this.setState({ cartlinesErrors: {}, isCartValid: true });
        }

        return AsyncResult.resolve();
    }

    /**
     * Get order quantity limit feature flag.
     * @returns True if flag is enabled, false otherwise.
     */
    private async getOrderQuantityLimitsFeatureFlag(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        try {
            const isQuantityLimitsFeatureEnabledInHq =
                featureStatuses.find(
                    featureState => featureState.Name === 'Dynamics.AX.Application.RetailDefaultOrderQuantityLimitsFeature'
                )?.IsEnabled || false;
            if (!isQuantityLimitsFeatureEnabledInHq) {
                return false;
            }
        } catch (error) {
            this.props.telemetry.warning(error);
            this.props.telemetry.debug('Unable to get feature states');
            return false;
        }

        const useSiteBuilderSettings = featureStatuses?.find(
            featureState => featureState.Name === 'RevertToSiteBuilderOrderQuantityLimitsSettings'
        )?.IsEnabled;

        if (useSiteBuilderSettings) {
            const defaultOrderQuantityLimitsFeatureConfig = this.props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits;
            if (defaultOrderQuantityLimitsFeatureConfig === 'none') {
                return false;
            }

            if (defaultOrderQuantityLimitsFeatureConfig === 'all') {
                return true;
            }

            try {
                const customerInfo = await this.props.data.customerInformation;
                return (
                    !!customerInfo &&
                    ((defaultOrderQuantityLimitsFeatureConfig === 'b2b' && customerInfo.IsB2b) ||
                        (defaultOrderQuantityLimitsFeatureConfig === 'b2c' && !customerInfo.IsB2b))
                );
            } catch (error) {
                this.props.telemetry.warning(error);
                this.props.telemetry.debug('Unable to get customer info');
                return false;
            }
        } else {
            const cartConfiguration = await this.props.data.cartConfiguration;
            return !(cartConfiguration?.IgnoreOrderSettings ?? false);
        }
    }

    private async _isChannelLimitsFeatureEnabled(): Promise<boolean> {
        const featureStatuses = await this.props.data.featureState;

        const isChannelLimitsFeatureEnabledInHq = featureStatuses?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.EcommerceQuantityLimitConfigurationsFeature'
        )?.IsEnabled;

        return isChannelLimitsFeatureEnabledInHq ?? false;
    }

    /**
     * Gets the cart lines group.
     * @returns ICartlinesViewProps [][].
     */
    private getCartLinesGroup(): ICartlinesViewProps[][] | null {
        const {
            data: {
                cart: { result: cart },
                wishlists: { result: wishlists },
                orderTemplates: { result: orderTemplates },
                customerInformation: { result: customerInformation }
            },
            context: {
                request: { channel: { PickupDeliveryModeCode } = { PickupDeliveryModeCode: undefined } }
            },
            config,
            telemetry,
            resources,
            modules
        } = this.props;

        const imageSettings = this.getImageSettings(config.imageSettings);
        const cartlinesErrors: CartLineValidationResultsByLineId = this.state.cartlinesErrors;
        let storeSelectorId: string = '';
        if (modules && Object.keys(modules).length > 0 && modules.storeSelector && modules.storeSelector.length > 0) {
            storeSelectorId = modules.storeSelector[0].id;
        }

        const RetailMultiplePickupFeatureState = this.props.data.featureState.result?.find(
            featureState => featureState.Name === 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature'
        );
        const multiplePickUpEnabled = RetailMultiplePickupFeatureState?.IsEnabled;
        const { featureState } = this.props.data;
        const channelMultipleCatalogsFeature = 'Dynamics.AX.Application.ChannelMultipleCatalogsFeature';
        const isMultipleCatalogsFeatureEnabled =
            featureState.result?.find((item: FeatureState) => item.Name === channelMultipleCatalogsFeature)?.IsEnabled || false;

        return this.props.data.products &&
            (cart?.hasInvoiceLine || this.props.data.products.result) &&
            cart &&
            cart.cart.CartLines &&
            cart.cart.CartLines.length > 0 &&
            this.props.data.channelDeliveryOptionConfig?.result
            ? CartLineGroupItems({
                  cartlines: cart.cart.CartLines,
                  storeSelectorModuleId: storeSelectorId,
                  cartlinesErrors,
                  products: this.props.data.products.result,
                  productAvailabilites: this._getProductAvailabilities(),
                  productDeliveryOptions: undefined,
                  catalogs: this.props.data.catalogs?.result,
                  imageSettings,
                  isStockCheckEnabled: this.props.context.app.config.enableStockCheck || false,
                  hasInvoiceLine: cart?.hasInvoiceLine || false,
                  maxCartlineQuantity: updateMaxQuantityForCartLineItem(this.props.context.app.config.maxQuantityForCartLineItem),
                  gridSettings: this.props.context.request.gridSettings!,
                  context: this.props.context,
                  retailMulitplePickupFeatureState: multiplePickUpEnabled,
                  typeName: this.props.typeName,
                  id: this.props.id,
                  resources: {
                      sizeString: resources.productDimensionTypeSize,
                      colorString: resources.productDimensionTypeColor,
                      configString: resources.productDimensionTypeConfiguration,
                      styleString: resources.productDimensionTypeStyle,
                      amountString: resources.productDimensionTypeAmount,
                      quantityDisplayString: resources.quantityDisplayText,
                      inputQuantityAriaLabel: resources.inputQuantityAriaLabel,
                      decrementButtonAriaLabel: resources.decrementButtonAriaLabel,
                      incrementButtonAriaLabel: resources.incrementButtonAriaLabel,
                      discountStringText: resources.discountStringText,
                      originalPriceText: resources.originalPriceText,
                      currentPriceText: resources.currentPriceText,
                      shippingChargesText: resources.shippingCharges,
                      salesAgreementPricePrompt: resources.salesAgreementPricePrompt,
                      priceEditorResources: {
                          ...resources
                      },
                      depositSuffixText: resources.depositSuffixText
                  },
                  telemetry,
                  addToWishlistButtonText: resources.addToWishlistButtonText,
                  removeFromWishlistButtonText: resources.removeFromWishlistButtonText,
                  removeButtonText: resources.removeCartButtonText,
                  shipItText: resources.shipInsteadDisplayText,
                  pickitUpText: resources.pickItUpDisplayText,
                  changeStoreText: resources.changeStoreDisplayText,
                  storeSelectorStateManager: this.props.data.storeSelectorStateManager.result,
                  outOfStockText: resources.outOfStockErrorMessage,
                  outOfRangeOneText: resources.errorMessageOutOfRangeOneLeft,
                  outOfRangeFormatText: resources.errorMessageOutOfRangeFormat,
                  cartState: this.props.data.cart.result,
                  orgUnitLocations: this.props.data.orgUnitLocations.result,
                  removeItemClickHandler: this.removeItemFromCartHandler,
                  moveToWishlistSuccessHandler: this.moveToWishlistSuccessHandler,
                  updateCartLinesQuantitySuccessHandler: this.updateCartlineQuantity,
                  locationChangedHandler: this.fulfillmentStoreIdUpdated,
                  wishlists,
                  defaultWishlistName: resources.defaultWishlistName,
                  pickupDeliveryModeCode: PickupDeliveryModeCode,
                  includeErrors: config.showOutOfStockErrors,
                  showShippingChargesForLineItems: config.showShippingChargesForLineItems,
                  telemetryContent: this.telemetryContent,
                  isQuantityLimitsFeatureEnabled: this.isQuantityLimitsFeatureEnabled,

                  addToOrderTemplateDialogResources: this._generateAddToOrderTemplateDialogResources(resources),
                  createOrderTemplateDialogResources: this._generateOrderTemplateNameDialogResources(resources),
                  itemAddedToOrderTemplateDialogResources: this._generateItemAddedToOrderTemplateDialogResources(resources),
                  duplicateItemsWhenAddingToOrderTemplateDialogResources: this._generateDuplicateItemsWhenAddingToOrderTemplateDialogResources(
                      resources
                  ),

                  addToOrderTemplateButtonText: resources.addToOrderTemplateButtonText,
                  addToOrderTemplateButtonTooltip: resources.addToOrderTemplateButtonTooltip,
                  addToOrderTemplateMessage: resources.addToOrderTemplateMessage,
                  addItemToOrderTemplateError: resources.addItemToOrderTemplateError,
                  orderTemplates,
                  customerInformation,
                  channelDeliveryOptionConfig: this.props.data.channelDeliveryOptionConfig?.result,
                  shouldIgnoreWarehouse: this.props.context.app.config.warehouseAggregation === 'multiple',
                  productAvailability: this.props.data.productAvailabilites,
                  productPickUpOptions: this.state.productPickUpOptions,
                  isMultipleCatalogFeatureEnabledInHq: isMultipleCatalogsFeatureEnabled || false,
                  unitConversions: this.state.unitConversions
              })
            : null;
    }

    private _getProductAvailabilities(): IProductInventoryInformation[] {
        const productAvailabilities = this.props.data.productAvailabilites.result || [];

        if (this.props.data.productAvailabilites.status === 'FAILED') {
            this.props.data.cart.result?.cart.CartLines?.forEach((line) => {
                productAvailabilities?.push({
                    IsProductAvailable: false,
                    StockLevelCode: 'ERR',
                    StockLevelLabel: this.props.resources.availabilityError,
                    ProductAvailableQuantity: {
                        ProductId: line.ProductId
                    }
                });
            });
        }

        return productAvailabilities;
    }

    private _onBreakoffChange = (product: ProductListProduct, enabled: boolean) => {
        if (!product.RecordId) {
            return;
        }

        this.setState((prevState: ICartComponentState) => ({
            breakoffProducts: ({ ...prevState.breakoffProducts, [product.RecordId]: enabled })
        }));
    };

    private _onUpdateSelection = (selection: IProductSelection) => {
        this.setState({ selectedProducts: selection });
    };

    private _onUpdateOrderTemplates = async (product?: ProductListProduct, newOrderTemplate?: ProductList): Promise<OrderTemplate[]> => {
        const { context, config } = this.props;
        const { orderTemplates } = this.state;
        let newOrderTemplates;

        if (newOrderTemplate) {
            newOrderTemplates = [...(orderTemplates ? orderTemplates : [])];

            // Add new order template if it doesn't exist
            if (newOrderTemplates.every(x => x.productList.Id !== newOrderTemplate.Id)) {
                newOrderTemplates.splice(0, 0, new OrderTemplate(newOrderTemplate, [], 0));
            }

            if (product) {
                const newProducts = [...this.props.data.products.result || []];
                const newProduct = newProducts?.find(x => x.RecordId === product.RecordId);

                if (newProduct) {
                    const newPartOfProductList: IProductListByProductId = {
                        ProductId: newProduct.RecordId,
                        ProductListId: newOrderTemplate?.Id,
                        Name: newOrderTemplate?.Name
                    };
                    if (!newProduct.PartOfProductLists) {
                        newProduct.PartOfProductLists = [];
                    }
                    newProduct.PartOfProductLists.push(newPartOfProductList);
                }

                this.props.context.actionContext.update(new ActiveCartProductsInput(), newProducts);
            }
        } else {
            // Refresh order templates incase new order template was created
            const queryResultSettings = QueryResultSettingsProxy.fromInputData({
                requestContext: context.request,
                config: config
            }).QueryResultSettings;
            const input = new GetOrderTemplatesInput('', queryResultSettings);
            newOrderTemplates = (await getOrderTemplates(input, context.actionContext)).map(x => new OrderTemplate(x, [], 0));
        }

        this.setState({ orderTemplates: newOrderTemplates });
        return newOrderTemplates;
    };

    private _retrieveOrderTemplates = async (): Promise<OrderTemplate[]> => {
        return this.state.orderTemplates ? this.state.orderTemplates : this._onUpdateOrderTemplates();
    };
}

export default Cart;
